'use client'

import { useAtom } from 'jotai'
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation'
import { useMemo } from 'react'
import { type NavigationItem, navigationHistoryAtom } from './atoms'

export const useCurrentNavigationItem = (): NavigationItem => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const pathParams = useParams()

  const page = useMemo(() => {
    let page = pathname
    Object.values(pathParams).forEach((value) => {
      page = page.replace(`/${value}`, '')
    })
    return page
  }, [pathname, pathParams])

  return {
    page,
    path: pathname,
    searchParams,
    pathParams,
  }
}

export const useNavigationHistory = () => {
  const { page } = useCurrentNavigationItem()
  const router = useRouter()

  const [navigationHistory, setNavigationHistory] = useAtom(
    navigationHistoryAtom,
  )
  const getPreviousPageItem = () => {
    return navigationHistory.findLast(
      (historyItem) => historyItem.page !== page,
    )
  }

  const getPreviousPageUrl = () => {
    const item = getPreviousPageItem()
    return item ? `${item.path}?${item.searchParams.toString()}` : '/'
  }

  const pushPath = (item: NavigationItem | string) => {
    let newItem: NavigationItem
    if (typeof item === 'string') {
      const url = new URL(item)
      newItem = {
        page: url.pathname,
        path: url.pathname,
        searchParams: url.searchParams,
        pathParams: {},
      }
    } else {
      newItem = item
    }
    setNavigationHistory((history) => [...history.slice(-9), newItem])
  }

  const navigateToPreviousPage = () => {
    const url = getPreviousPageUrl()
    const previousPage = getPreviousPageItem()
    const previousPageIndex = navigationHistory.findIndex(
      (item) => item === previousPage,
    )
    setNavigationHistory((history) =>
      history.slice(0, previousPageIndex === 0 ? 1 : previousPageIndex),
    )
    router.push(url, {
      scroll: false,
    })
  }

  return { pushPath, navigateToPreviousPage }
}
